import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { OAuthService } from 'angular-oauth2-oidc';
import { first, map } from 'rxjs/operators';

import { RootState } from '@root/app.state';
import * as PlatformActions from '@platform/platform.actions';
import { selectUser } from '@platform/platform.selectors';
import { CurrentUser } from '@platform/platform.state';
import { RouteGuard } from '@platform/routing/route.guard';
import { ResourceState, ResourceStatuses } from '@platform/shared/resource.state';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthenticateGuard implements RouteGuard {
  constructor(
    private oauthService: OAuthService,
    private store$: Store<RootState>,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return (await this.isAuthenticated(state)) && (await this.ensureUser());
  }

  async canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    return (await this.isAuthenticated(state)) && (await this.ensureUser());
  }

  private async isAuthenticated(state: RouterStateSnapshot): Promise<boolean> {
    const hasIdToken = this.oauthService.hasValidIdToken();

    if (!hasIdToken) {
      if (!this.oauthService.loginUrl) {
        await this.oauthService.loadDiscoveryDocument();
      }
      this.oauthService.initLoginFlow(state.url);
    }

    return hasIdToken;
  }

  private async ensureUser(): Promise<boolean> {
    const user = await this.getUser();
    if (!user) {
      const userState = await this.loadUser();
      return userState.status === ResourceStatuses.SUCCEEDED;
    }

    return true;
  }

  private async getUser(): Promise<CurrentUser> {
    return await firstValueFrom(
      this.store$.pipe(
        select(selectUser),
        first((state) => state.status !== ResourceStatuses.PENDING), // an closed observable is required to make the promise returning
        map((state) => state.data),
      ),
    );
  }

  private async loadUser(): Promise<ResourceState<CurrentUser>> {
    this.store$.dispatch(PlatformActions.loadCurrentUser());

    return await firstValueFrom(
      this.store$.pipe(
        select(selectUser),
        first((x) => x.status === ResourceStatuses.FAILED || x.status === ResourceStatuses.SUCCEEDED),
      ),
    );
  }
}
